import { PaymentContext, PaymentMethod } from '@api/READ_ONLY/visits_api/Api';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import type { TFunction } from 'i18next';

export type VisitPaymentStatus = 'none' | 'pending' | 'unpaid' | 'paid';

/**
 * Derive a payment status from a visit's paymentContext. Pure — no i18n.
 * The backend returns a null context when there is nothing to collect, a
 * populated context that is owed-but-not-yet-due ("pending"), owed-and-due
 * ("unpaid"), or fully settled ("paid").
 *
 * EVERY AMOUNT IN THE CONTEXT IS CYCLE-WIDE, so an owed state belongs to the visit
 * that CLOSES the cycle and to no other. A `collect_every_n = 14` request used to
 * print "€140 Pending" on all fourteen of its rows, which reads as fourteen
 * separate debts. `isCollectionVisit` is the backend's answer to "is it this
 * visit's turn"; mobile's derivePaymentState applies the same rule.
 *
 * 'paid' is deliberately NOT suppressed: it is settled money, it is the same
 * answer across the cycle, and hiding it would read as unpaid.
 */
export function getVisitPaymentStatus(ctx?: PaymentContext | null): VisitPaymentStatus {
  if (!ctx) return 'none';
  if ((ctx.outstandingAmount ?? 0) <= 0) return 'paid';
  // Strict `=== false`: an absent flag is a backend that predates it, and keeping
  // the old (noisier) display beats hiding money that someone has to collect.
  if (ctx.isCollectionVisit === false) return 'none';
  return ctx.paymentRequired ? 'unpaid' : 'pending';
}

function methodLabel(ctx: PaymentContext | null | undefined, t: TFunction): string | null {
  switch (ctx?.defaultPaymentMethod) {
    case PaymentMethod.Cash:
      return t('service-requests-option-cash');
    case PaymentMethod.Card:
      return t('service-requests-option-card');
    default:
      return null;
  }
}

/**
 * Human-readable payment display for a visit.
 * compact  (list):  "Cash · €40.00 Unpaid" | "Cash · €40.00 Pending" | "Cash · Paid" | "-"
 * verbose  (form):  "Cash · €40.00 outstanding — Unpaid" | "Cash · €40.00 outstanding — Pending" | ...
 */
export function formatVisitPayment(
  ctx: PaymentContext | null | undefined,
  t: TFunction,
  opts?: { verbose?: boolean }
): string {
  const status = getVisitPaymentStatus(ctx);
  if (status === 'none') return '-';

  const parts: string[] = [];
  const method = methodLabel(ctx, t);
  if (method) parts.push(method);

  if (status === 'paid') {
    parts.push(t('visits-payment-paid'));
  } else {
    // 'unpaid' (due now) or 'pending' (accruing, not yet due).
    const amount = currencyFormatter(ctx?.outstandingAmount ?? 0);
    const statusLabel = status === 'unpaid' ? t('visits-payment-unpaid') : t('visits-payment-pending');
    parts.push(
      opts?.verbose ? `${amount} ${t('visits-payment-outstanding')} — ${statusLabel}` : `${amount} ${statusLabel}`
    );
  }

  return parts.join(' · ');
}
